import { cx } from 'class-variance-authority';
import { cloneElement, type JSX, useContext, useRef } from 'react';
import {
  type AriaCheckboxGroupItemProps,
  mergeProps,
  useCheckboxGroupItem,
  useFocusRing,
  VisuallyHidden,
} from 'react-aria';
import { type CheckboxGroupState } from 'react-stately';

import { CheckboxGroupContext } from './CheckboxGroup';

interface ProductSelectCheckboxProps
  extends Omit<AriaCheckboxGroupItemProps, 'children'> {
  readonly children: (state: CheckboxGroupState | null) => JSX.Element;
  readonly className?: string;
}

function ProductSelectCheckbox({
  children,
  className,
  ...props
}: ProductSelectCheckboxProps): JSX.Element {
  const state = useContext(CheckboxGroupContext);
  const ref = useRef(null);
  const { inputProps } = useCheckboxGroupItem(props, state!, ref);
  const { focusProps } = useFocusRing();
  const isSelected = state?.isSelected(props.value);

  return (
    <label
      className={cx(
        'block cursor-pointer overflow-hidden rounded-xl border p-5',
        {
          'border-gray-600': isSelected,
          'border-gray-300 hover:border-gray-600/30': !isSelected,
        },
        className,
      )}
    >
      <VisuallyHidden>
        <input {...mergeProps(inputProps, focusProps)} ref={ref} />
      </VisuallyHidden>
      {cloneElement(children(state))}
    </label>
  );
}

export default ProductSelectCheckbox;
